
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../contexts/CartContext';
import { CloseIcon } from './icons';
import { Language } from '../types';
import { t } from '../translations';
import { useSettings } from '../src/contexts/SettingsContext';
import { DEFAULT_MENU_ITEM_IMAGE } from '../src/constants';
import { Clock, Check, X, Package, ShoppingBag, Utensils, Truck, CreditCard, Banknote, User, Phone } from 'lucide-react';
import { useScrollLock } from '../src/utils/useScrollLock';
import { OrderType, PaymentMethod } from '../types';
import CartUpsell from './CartUpsell';
import { trackFunnel } from '../src/utils/funnel';
import { isRateLimited } from '../src/utils/rateLimitedWrite';

interface CartWidgetProps {
    language: Language;
    tableNumber: string;
    /**
     * Режим публичного самовывоза (маршрут /takeaway).
     * В этом режиме стола нет, orderType всегда 'takeaway',
     * показываются поля phone + pickupTime.
     */
    takeawayMode?: boolean;
}

import { toast } from 'sonner';
import { plural } from '../src/utils/plural';
import { tableLabel } from '../src/utils/tableLabel';

const CartWidget: React.FC<CartWidgetProps> = ({ language, tableNumber, takeawayMode = false }) => {
    const { totalItems, totalPrice, cart, addToCart, removeFromCart, clearCart, sendOrder, verifyStaffPin, currentOrder } = useCart();
    const { settings } = useSettings();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [showReceipt, setShowReceipt] = useState(false);
    const [lastOrderData, setLastOrderData] = useState<{ items: any[], total: number } | null>(null);

    // Checkout options
    const [orderType, setOrderType] = useState<OrderType>(takeawayMode ? 'takeaway' : 'dine_in');
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');

    // Синк orderType с режимом. Без этого — если гость сначала зашёл на
    // /takeaway, а потом через history.back() пошёл на /table/5, widget
    // не ремонтится и state orderType застрял бы на 'takeaway'.
    React.useEffect(() => {
        setOrderType(takeawayMode ? 'takeaway' : 'dine_in');
    }, [takeawayMode]);
    const [pickupMinutes, setPickupMinutes] = useState<30 | 60 | 90>(30);
    const [notes, setNotes] = useState('');
    const [staffPin, setStaffPin] = useState('');
    const [staffInfo, setStaffInfo] = useState<{name: string, discount: number} | null>(null);
    const [pinChecking, setPinChecking] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [promoCode, setPromoCode] = useState('');
    const [promoInfo, setPromoInfo] = useState<{ type: 'percent' | 'fixed'; value: number } | null>(null);
    const [promoChecking, setPromoChecking] = useState(false);
    const [promoError, setPromoError] = useState(false);

    const handlePinChange = async (pin: string) => {
        // Only allow digits, max 6
        const cleaned = pin.replace(/\D/g, '').slice(0, 6);
        setStaffPin(cleaned);
        setStaffInfo(null);

        if (cleaned.length === 6) {
            setPinChecking(true);
            const result = await verifyStaffPin(cleaned);
            if (result) {
                setStaffInfo({ name: result.name, discount: result.discount });
            } else {
                setStaffInfo(null);
            }
            setPinChecking(false);
        }
    };

    // Локальный preview итоговой суммы — повторяет серверную логику
    // /api/validate-order: сначала staff %, потом promo. Сервер всё равно
    // пересчитывает заново, эта цифра только для UX.
    // Парсим nutrition-строки (часто "120 ккал" / "12 г") в number,
    // суммируем по cart с учётом quantity. Если у блюда нет nutrition — пропускаем.
    const parseNum = (s?: string): number => {
        if (!s) return 0;
        const m = String(s).match(/[\d.]+/);
        return m ? parseFloat(m[0]) : 0;
    };
    const nutritionTotals = cart.reduce(
        (acc, item) => {
            const q = item.quantity || 1;
            acc.cal += parseNum(item.nutrition?.calories) * q;
            acc.protein += parseNum(item.nutrition?.protein) * q;
            acc.fat += parseNum(item.nutrition?.fat) * q;
            acc.carbs += parseNum(item.nutrition?.carbs) * q;
            return acc;
        },
        { cal: 0, protein: 0, fat: 0, carbs: 0 }
    );
    const hasNutrition = cart.some((i) => !!i.nutrition);

    const afterStaff = staffInfo ? totalPrice * (1 - staffInfo.discount / 100) : totalPrice;
    const afterPromo = promoInfo
        ? promoInfo.type === 'percent'
            ? afterStaff * (1 - promoInfo.value / 100)
            : Math.max(0, afterStaff - promoInfo.value)
        : afterStaff;
    const itemsWord = plural(
    totalItems,
    t('cart.itemsOne', language),
    t('cart.itemsFew', language),
    t('cart.items', language)
  );
  const confirmWord = plural(
    totalItems,
    t('cart.confirmItemsOne', language),
    t('cart.confirmItemsFew', language),
    t('cart.confirmItems', language)
  );
  const discountedTotal = Math.round(afterPromo);

    const handlePromoApply = async () => {
        const code = promoCode.trim().toUpperCase();
        if (!code) return;
        setPromoChecking(true);
        setPromoError(false);
        try {
            const res = await fetch('/api/promo/validate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code }),
            });
            const data = await res.json();
            if (data?.valid) {
                setPromoInfo({ type: data.type, value: data.value });
                setPromoCode(code);
            } else {
                setPromoInfo(null);
                setPromoError(true);
            }
        } catch {
            setPromoInfo(null);
            setPromoError(true);
        } finally {
            setPromoChecking(false);
        }
    };

    const handlePromoRemove = () => {
        setPromoCode('');
        setPromoInfo(null);
        setPromoError(false);
    };

    const handleSendOrder = () => {
        setShowConfirm(true);
    };

    const doSendOrder = async () => {
        setIsSending(true);
        const orderData = { items: [...cart], total: discountedTotal };
        try {
            // Для takeaway считаем целевое время самовывоза
            const pickupAt = takeawayMode
                ? new Date(Date.now() + pickupMinutes * 60 * 1000)
                : null;
            const trimmedPhone = customerPhone.trim();
            const orderId = await sendOrder(
                orderType,
                paymentMethod,
                customerName.trim(),
                'regular',
                tableNumber,
                notes,
                staffPin.length === 6 ? staffPin : undefined,
                takeawayMode && trimmedPhone ? trimmedPhone : undefined,
                pickupAt,
                promoInfo && promoCode ? promoCode : undefined
            );
            setLastOrderData(orderData);

            // Онлайн-оплата временно отключена (см. payment-method UI выше) —
            // ветка paymentMethod==='online' удалена. Кэш cart для recovery
            // больше не нужен. Если потом включим обратно — флоу был такой:
            //   localStorage.setItem('pending_payment_cart', JSON.stringify(cart));
            //   fetch('/api/payment/init', ...).then(({url}) => window.location.href = url);

            toast.success(t('cart.orderSent', language));
            setIsOpen(false);
            // Conversion funnel: успешная отправка заказа.
            trackFunnel('order_sent');
            // Гость попадает на /order/:id — там прогресс и push при ready.
            navigate(`/order/${orderId}`);
        } catch (e: any) {
            // Логируем только message, чтобы PII (телефон/имя клиента) из request
            // не попало в браузерный лог и Sentry breadcrumbs.
            console.error("Order error:", e?.code || e?.message || 'unknown');
            // Отказ правил на этом пути — это анти-спам лимит, а не поломка:
            // объясняем человеку, что надо просто подождать.
            toast.error(isRateLimited(e) ? t('cart.tooFast', language) : t('cart.error', language));
        } finally {
            setIsSending(false);
        }
    };

    const handleFinish = () => {
        setShowReceipt(false);
        setIsOpen(false);
    };

    useScrollLock(isOpen || showReceipt);

    const displayItems = showReceipt && (currentOrder || lastOrderData) ? (currentOrder?.items || lastOrderData?.items || []) : cart;
    const displayTotal = showReceipt && (currentOrder || lastOrderData) ? (currentOrder?.total || lastOrderData?.total || 0) : totalPrice;

    const showStatusButton = currentOrder && currentOrder.status !== 'completed' && currentOrder.status !== 'cancelled';
    const showCartButton = totalItems > 0;

    return (
        <>
            {/* Floating Bar — combined status + cart */}
            {!isOpen && !showReceipt && (showStatusButton || showCartButton) && (
                <motion.div
                    initial={{ y: 60, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 60, opacity: 0 }}
                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    className="fixed bottom-[118px] left-3 right-3 z-[45] pointer-events-auto"
                >
                    {/* Status bar (small, above cart) */}
                    {showStatusButton && (
                        <button
                            onClick={() => setShowReceipt(true)}
                            className="w-full mb-1.5 bg-[var(--bg-card-90)] backdrop-blur-xl border border-[var(--glass-border)] rounded-xl px-3 py-2 flex items-center gap-2 shadow-sm active:scale-[0.98] transition-all"
                        >
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                                currentOrder?.status === 'accepted' ? 'bg-info-bg-50 text-info-fg' :
                                currentOrder?.status === 'ready' ? 'bg-success-bg-50 text-success-fg' :
                                'bg-accent-10 text-accent'
                            }`}>
                                {currentOrder?.status === 'new' && <div className="w-3 h-3 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>}
                                {currentOrder?.status === 'accepted' && <Clock className="w-3 h-3" />}
                                {currentOrder?.status === 'ready' && <Package className="w-3 h-3" />}
                            </div>
                            <span className="text-xs font-bold text-[var(--accent-color)]">{t(`cashier.status.${currentOrder?.status}`, language)}</span>
                            <span className="text-xs text-[var(--text-secondary)] ml-auto">{currentOrder?.total} {settings.currency}</span>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-3 h-3 text-[var(--text-secondary)]">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                            </svg>
                        </button>
                    )}
                    {/* Cart bar */}
                    {showCartButton && (
                        <button
                            onClick={() => { setIsOpen(true); trackFunnel('cart_open', { dedupePerDay: true }); }}
                            className="w-full bg-[var(--accent-color)] text-white rounded-2xl shadow-[0_4px_15px_rgba(255,140,0,0.3)] active:scale-[0.98] transition-all"
                        >
                            <div className="flex items-center justify-between px-4 py-3">
                                <div className="flex items-center gap-3">
                                    <div className="bg-white/20 rounded-lg w-8 h-8 flex items-center justify-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                                            <path fillRule="evenodd" d="M7.5 6v.75H5.513c-.96 0-1.764.724-1.865 1.679l-1.263 12A1.875 1.875 0 004.25 22.5h15.5a1.875 1.875 0 001.865-2.071l-1.263-12a1.875 1.875 0 00-1.865-1.679H16.5V6a4.5 4.5 0 10-9 0zM12 3a3 3 0 00-3 3v.75h6V6a3 3 0 00-3-3zm-3 8.25a3 3 0 106 0v-.75a.75.75 0 011.5 0v.75a4.5 4.5 0 11-9 0v-.75a.75.75 0 011.5 0v.75z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="text-left">
                                        <p className="font-bold text-sm leading-none">{t('cart.title', language)}</p>
                                        <p className="text-white text-[11px]">{totalItems} {itemsWord}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    {/* key={totalItems} ремонтит span на каждое изменение корзины
                                        → запускает initial→animate с bounce-эффектом. Микро-feedback
                                        что товар точно добавился. */}
                                    <motion.span
                                        key={totalItems}
                                        initial={{ scale: 1.25 }}
                                        animate={{ scale: 1 }}
                                        transition={{ type: 'spring', stiffness: 500, damping: 15 }}
                                        className="font-black text-base inline-block"
                                    >
                                        {totalPrice} {settings.currency}
                                    </motion.span>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                </div>
                            </div>
                        </button>
                    )}
                </motion.div>
            )}

            {/* Bottom Sheet / Receipt */}
            <AnimatePresence>
                {(isOpen || showReceipt) && (
                    <>
                        <motion.div
                            key="cart-backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0, pointerEvents: 'none' }}
                            onClick={() => {
                                if (!showReceipt) setIsOpen(false);
                            }}
                            className="fixed inset-0 bg-black/70 backdrop-blur-[6px] z-[150] cursor-pointer pointer-events-auto"
                        />
                        <motion.div
                            key="cart-sheet"
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            exit={{ y: "100%" }}
                            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                            className="fixed bottom-0 left-0 right-0 z-[160] bg-[var(--bg-card)] rounded-t-[40px] border-t border-[var(--glass-border)] shadow-2xl max-h-[92vh] flex flex-col overflow-hidden pointer-events-auto"
                        >
                        {!showReceipt ? (
                                <>
                                    <div className="flex items-center justify-between p-7 border-b border-[var(--bg-tertiary)]">
                                        <div className="flex flex-col">
                                            <h2 className="text-2xl font-serif font-bold text-[var(--text-primary)] leading-none">
                                                {t('cart.title', language)}
                                            </h2>
                                            <span className="text-sm text-[var(--text-secondary)] mt-1">{totalItems} {itemsWord}</span>
                                        </div>
                                        <button onClick={() => setIsOpen(false)} className="p-3 bg-[var(--bg-tertiary)] rounded-full hover:bg-[var(--accent-color)] hover:text-white transition-all active:scale-90">
                                            <CloseIcon className="w-6 h-6" />
                                        </button>
                                    </div>

                                    <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                                        {/* Empty cart state — гость удалил все позиции из чекаута,
                                            показываем дружелюбную заглушку с CTA «вернуться в меню». */}
                                        {!showReceipt && cart.length === 0 && (
                                            <div className="py-12 flex flex-col items-center text-center">
                                                <div className="relative mb-5">
                                                    <div className="w-20 h-20 rounded-full bg-accent-10 flex items-center justify-center">
                                                        <ShoppingBag className="w-10 h-10 text-accent" />
                                                    </div>
                                                </div>
                                                <h3 className="text-xl font-bold text-ink mb-1">
                                                    {t('cart.emptyTitle', language)}
                                                </h3>
                                                <p className="text-sm text-ink-soft max-w-xs mb-6">
                                                    {t('cart.emptySubtitle', language)}
                                                </p>
                                                <button
                                                    onClick={() => setIsOpen(false)}
                                                    className="px-6 py-3 bg-accent text-white rounded-card-md font-bold text-sm shadow-accent-glow active:scale-95 transition-transform"
                                                >
                                                    {t('checkout.backToMenu', language)}
                                                </button>
                                            </div>
                                        )}

                                        {/* Cart Items */}
                                        <div className="space-y-4">
                                            {cart.map((item, idx) => {
                                                const itemAddonKey = (item.selectedAddons || []).map(a => a.names.en).sort().join(',');
                                                const itemAddonTotal = (item.selectedAddons || []).reduce((s, a) => s + a.price, 0);
                                                const itemTotal = (item.price + itemAddonTotal) * item.quantity;
                                                return (
                                                <motion.div
                                                    layout
                                                    key={`${item.id}-${itemAddonKey || idx}`}
                                                    className="flex items-center justify-between bg-[var(--bg-primary)] p-4 rounded-3xl shadow-sm border border-[var(--glass-border)] hover:border-[var(--accent-20)] transition-colors"
                                                >
                                                    <div className="flex items-center space-x-4">
                                                        <div className="relative">
                                                            <img
                                                                src={item.image || DEFAULT_MENU_ITEM_IMAGE}
                                                                alt=""
                                                                loading="lazy"
                                                                decoding="async"
                                                                className="w-20 h-20 rounded-2xl object-cover shadow-md"
                                                                referrerPolicy="no-referrer"
                                                                onError={(e) => {
                                                                    (e.target as HTMLImageElement).src = DEFAULT_MENU_ITEM_IMAGE;
                                                                }}
                                                            />
                                                            <span className="absolute -top-2 -right-2 bg-[var(--accent-color)] text-white text-[10px] font-bold px-2 py-1 rounded-lg shadow-sm">
                                                                x{item.quantity}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <p className="font-bold text-[var(--text-primary)] text-lg leading-tight">
                                                                {item.names?.[language] || item.names?.ru}
                                                            </p>
                                                            {item.selectedAddons && item.selectedAddons.length > 0 && (
                                                                <div className="flex flex-wrap gap-1 mt-1">
                                                                    {item.selectedAddons.map((a, i) => (
                                                                        <span key={i} className="text-[9px] font-bold bg-[var(--bg-tertiary)] text-[var(--text-secondary)] px-1.5 py-0.5 rounded">
                                                                            + {a.names?.[language] || a.names?.ru}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {language !== 'ru' && (
                                                                <p className="text-xs text-[var(--text-secondary)] italic mt-1 opacity-70">
                                                                    {item.names?.ru}
                                                                </p>
                                                            )}
                                                            <p className="text-[var(--accent-color)] font-black text-lg mt-1">{itemTotal} {settings.currency}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col items-center bg-[var(--bg-card)] rounded-2xl border border-[var(--bg-tertiary)] overflow-hidden shadow-sm">
                                                        <button onClick={() => addToCart(item, item.selectedAddons)} className="w-10 h-10 flex items-center justify-center text-[var(--accent-color)] font-bold text-xl hover:bg-[var(--bg-secondary)] transition-colors">+</button>
                                                        <div className="h-[1px] w-full bg-[var(--bg-tertiary)]"></div>
                                                        <button onClick={() => removeFromCart(item.id, itemAddonKey)} className="w-10 h-10 flex items-center justify-center text-[var(--text-secondary)] hover:text-danger-fg font-bold text-xl hover:bg-[var(--bg-secondary)] transition-colors">-</button>
                                                    </div>
                                                </motion.div>
                                                );
                                            })}
                                        </div>

                                        {/* AI upsell: «к этому часто берут» — 2-3 предложения от Claude */}
                                        <CartUpsell language={language} hidden={showConfirm} />

                                        {/* Checkout Options */}
                                        <div className="space-y-6 pt-4 border-t border-[var(--bg-tertiary)]">
                                            {/* Order Type — скрыт в публичном takeaway-режиме */}
                                            {!takeawayMode && (
                                            <div className="space-y-3">
                                                <label className="text-sm font-black text-[var(--text-secondary)] uppercase tracking-widest pl-2">
                                                    {t('checkout.orderType', language)}
                                                </label>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <button
                                                        onClick={() => setOrderType('dine_in')}
                                                        className={`flex items-center justify-center space-x-2 p-4 rounded-2xl border-2 transition-all ${
                                                            orderType === 'dine_in'
                                                                ? 'bg-[var(--accent-color)] border-[var(--accent-color)] text-white shadow-lg shadow-[var(--accent-20)]'
                                                                : 'bg-[var(--bg-secondary)] border-transparent text-[var(--text-secondary)]'
                                                        }`}
                                                    >
                                                        <Utensils className="w-5 h-5" />
                                                        <span className="font-bold">{t('checkout.dineIn', language)}</span>
                                                    </button>
                                                    <button
                                                        onClick={() => setOrderType('takeaway')}
                                                        className={`flex items-center justify-center space-x-2 p-4 rounded-2xl border-2 transition-all ${
                                                            orderType === 'takeaway'
                                                                ? 'bg-[var(--accent-color)] border-[var(--accent-color)] text-white shadow-lg shadow-[var(--accent-20)]'
                                                                : 'bg-[var(--bg-secondary)] border-transparent text-[var(--text-secondary)]'
                                                        }`}
                                                    >
                                                        <ShoppingBag className="w-5 h-5" />
                                                        <span className="font-bold">{t('checkout.takeaway', language)}</span>
                                                    </button>
                                                </div>
                                            </div>
                                            )}

                                            {/* Takeaway-специфика: телефон + время самовывоза */}
                                            {takeawayMode && (
                                            <>
                                                <div className="space-y-3">
                                                    <label className="text-sm font-black text-[var(--text-secondary)] uppercase tracking-widest pl-2">
                                                        {t('checkout.phoneLabel', language)}
                                                    </label>
                                                    <div className="relative">
                                                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-secondary)]" />
                                                        <input
                                                            type="tel"
                                                            value={customerPhone}
                                                            onChange={(e) => setCustomerPhone(e.target.value)}
                                                            placeholder="+996 XXX XXX XXX"
                                                            className="w-full bg-[var(--bg-secondary)] border-2 border-transparent focus:border-[var(--accent-color)] rounded-2xl py-4 pl-12 pr-4 text-base font-bold text-[var(--text-primary)] outline-none transition-all"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="space-y-3">
                                                    <label className="text-sm font-black text-[var(--text-secondary)] uppercase tracking-widest pl-2">
                                                        {t('checkout.pickupWhen', language)}
                                                    </label>
                                                    <div className="grid grid-cols-3 gap-3">
                                                        {([30, 60, 90] as const).map((m) => (
                                                            <button
                                                                key={m}
                                                                onClick={() => setPickupMinutes(m)}
                                                                className={`flex flex-col items-center justify-center space-y-1 p-3 rounded-2xl border-2 transition-all ${
                                                                    pickupMinutes === m
                                                                        ? 'bg-[var(--accent-color)] border-[var(--accent-color)] text-white shadow-lg shadow-[var(--accent-20)]'
                                                                        : 'bg-[var(--bg-secondary)] border-transparent text-[var(--text-secondary)]'
                                                                }`}
                                                            >
                                                                <Clock className="w-5 h-5" />
                                                                <span className="font-bold text-xs">{t('checkout.pickupInMin', language).replace('{m}', String(m))}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                    <p className="text-xs text-[var(--text-secondary)] pl-2">
                                                        {t('checkout.readyBy', language).replace('{t}', new Date(Date.now() + pickupMinutes * 60000).toLocaleTimeString(language === 'ru' ? 'ru-RU' : language, { hour: '2-digit', minute: '2-digit' }))}
                                                    </p>
                                                </div>
                                            </>
                                            )}

                                            {/* Payment Method.
                                                Временно: оплата только наличными.
                                                Карта/онлайн скрыты до решения по интегратору. */}
                                            <div className="space-y-3">
                                                <label className="text-sm font-black text-[var(--text-secondary)] uppercase tracking-widest pl-2">
                                                    {t('checkout.paymentMethod', language)}
                                                </label>
                                                <div className="grid grid-cols-1 gap-3">
                                                    <button
                                                        onClick={() => setPaymentMethod('cash')}
                                                        className="flex items-center justify-center gap-2 p-3 rounded-2xl border-2 bg-[var(--accent-color)] border-[var(--accent-color)] text-white shadow-lg shadow-[var(--accent-20)]"
                                                    >
                                                        <Banknote className="w-5 h-5" />
                                                        <span className="font-bold text-sm">{t('checkout.cash', language)}</span>
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Customer Name */}
                                            <div className="space-y-3">
                                                <label className="text-sm font-black text-[var(--text-secondary)] uppercase tracking-widest pl-2">
                                                    {t('checkout.customerName', language)}
                                                </label>
                                                <div className="relative">
                                                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-secondary)]" />
                                                    <input
                                                        type="text"
                                                        value={customerName}
                                                        onChange={(e) => setCustomerName(e.target.value)}
                                                        placeholder={t('checkout.customerName', language)}
                                                        className="w-full bg-[var(--bg-secondary)] border-2 border-transparent focus:border-[var(--accent-color)] rounded-2xl py-4 pl-12 pr-4 text-base font-bold text-[var(--text-primary)] outline-none transition-all"
                                                    />
                                                </div>
                                            </div>

                                            {/* Notes */}
                                            <div className="space-y-3">
                                                <label className="text-sm font-black text-[var(--text-secondary)] uppercase tracking-widest pl-2">
                                                    {t('cashier.notes', language)}
                                                </label>
                                                <textarea
                                                    value={notes}
                                                    onChange={(e) => setNotes(e.target.value)}
                                                    placeholder="..."
                                                    className="w-full bg-[var(--bg-secondary)] border-2 border-transparent focus:border-[var(--accent-color)] rounded-2xl py-4 px-4 font-bold text-[var(--text-primary)] outline-none transition-all min-h-[100px] resize-none"
                                                />
                                            </div>

                                            {/* Staff PIN */}
                                            <div className="space-y-3">
                                                <label className="text-sm font-black text-[var(--text-secondary)] uppercase tracking-widest pl-2">
                                                    {t('cart.staffPin', language)}
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                        maxLength={6}
                                                        value={staffPin}
                                                        onChange={(e) => handlePinChange(e.target.value)}
                                                        placeholder={t('cart.staffPinPlaceholder', language)}
                                                        className="w-full bg-[var(--bg-secondary)] border-2 border-transparent focus:border-[var(--accent-color)] rounded-2xl py-4 pl-4 pr-12 font-bold text-[var(--text-primary)] outline-none transition-all tracking-[0.3em] text-center text-lg"
                                                    />
                                                    {pinChecking && (
                                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 border-2 border-[var(--accent-color)] border-t-transparent rounded-full animate-spin"></div>
                                                    )}
                                                </div>
                                                {staffPin.length === 6 && !pinChecking && staffInfo && (
                                                    <div className="bg-success-bg-50 border border-success-bg rounded-2xl p-3 flex items-center gap-3">
                                                        <div className="w-8 h-8 bg-success-bg-500 text-white rounded-full flex items-center justify-center text-xs font-black">%</div>
                                                        <div>
                                                            <p className="text-success-fg font-bold text-sm">{staffInfo.name}</p>
                                                            <p className="text-success-fg text-xs font-bold">{t('cart.staffDiscount', language)} {staffInfo.discount}%</p>
                                                        </div>
                                                    </div>
                                                )}
                                                {staffPin.length === 6 && !pinChecking && !staffInfo && (
                                                    <p className="text-danger-fg text-xs font-bold text-center">{t('cart.staffPinNotFound', language)}</p>
                                                )}
                                            </div>

                                            {/* Promo code UI временно скрыт: бизнес ещё не утвердил
                                                акции. Бэкенд /api/promo/validate работает, admin-страница
                                                /admin/promo доступна — включить обратно простым удалением
                                                этого `false &&`. См. docs/PROMO_CODES.md. */}
                                            {false && (
                                            <div className="space-y-3">
                                                <label className="text-sm font-black text-[var(--text-secondary)] uppercase tracking-widest pl-2">
                                                    {t('cart.promoCode', language)}
                                                </label>
                                                {promoInfo ? (
                                                    <div className="bg-success-bg-50 border border-success-bg rounded-2xl p-3 flex items-center justify-between gap-3">
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <div className="w-8 h-8 bg-success-bg-500 text-white rounded-full flex items-center justify-center text-xs font-black flex-shrink-0">%</div>
                                                            <div className="min-w-0">
                                                                <p className="text-success-fg font-black text-sm truncate">{promoCode}</p>
                                                                <p className="text-success-fg text-xs font-bold">
                                                                    -{promoInfo.type === 'percent' ? `${promoInfo.value}%` : `${promoInfo.value} ${settings.currency}`}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={handlePromoRemove}
                                                            className="text-xs font-bold text-success-fg underline flex-shrink-0"
                                                        >
                                                            {t('cart.promoRemove', language)}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex gap-2">
                                                        <input
                                                            type="text"
                                                            value={promoCode}
                                                            onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(false); }}
                                                            placeholder={t('cart.promoPlaceholder', language)}
                                                            maxLength={32}
                                                            className="flex-1 bg-[var(--bg-secondary)] border-2 border-transparent focus:border-[var(--accent-color)] rounded-2xl py-4 px-4 text-base font-bold text-[var(--text-primary)] outline-none transition-all tracking-wider text-center uppercase"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={handlePromoApply}
                                                            disabled={promoChecking || promoCode.trim().length < 3}
                                                            className="px-5 rounded-2xl bg-[var(--accent-color)] text-white font-black uppercase text-xs tracking-widest disabled:opacity-40"
                                                        >
                                                            {promoChecking ? '…' : t('cart.promoApply', language)}
                                                        </button>
                                                    </div>
                                                )}
                                                {promoError && (
                                                    <p className="text-danger-fg text-xs font-bold text-center">{t('cart.promoNotFound', language)}</p>
                                                )}
                                            </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="p-8 bg-[var(--bg-secondary)] pb-12 rounded-t-[40px] shadow-[0_-10px_40px_rgba(0,0,0,0.05)] border-t border-white/50">
                                        {/* Show discount if applied */}
                                        {(staffInfo || promoInfo) && (
                                            <div className="mb-4 space-y-2">
                                                <div className="flex justify-between text-sm text-[var(--text-secondary)]">
                                                    <span>{t('cart.originalTotal', language)}</span>
                                                    <span className="line-through">{totalPrice} {settings.currency}</span>
                                                </div>
                                                {staffInfo && (
                                                    <div className="flex justify-between text-sm text-success-fg font-bold">
                                                        <span>{t('cart.staffDiscount', language)} {staffInfo.discount}% ({staffInfo.name})</span>
                                                        <span>-{Math.round(totalPrice - afterStaff)} {settings.currency}</span>
                                                    </div>
                                                )}
                                                {promoInfo && (
                                                    <div className="flex justify-between text-sm text-success-fg font-bold">
                                                        <span>{t('cart.promoCode', language)} {promoCode} ({promoInfo.type === 'percent' ? `${promoInfo.value}%` : `${promoInfo.value} ${settings.currency}`})</span>
                                                        <span>-{Math.round(afterStaff - afterPromo)} {settings.currency}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div className="flex justify-between mb-3 text-2xl font-black text-[var(--text-primary)]">
                                            <span className="tracking-tight">{t('cart.total', language)}</span>
                                            <span className="text-[var(--accent-color)]">{discountedTotal} {settings.currency}</span>
                                        </div>
                                        {/* Nutrition сводка по корзине. Показывается только если у
                                            каких-то блюд есть nutrition-данные. Полезно для гостей,
                                            кто считает калории/КБЖУ. */}
                                        {hasNutrition && (
                                            <div className="mb-8 grid grid-cols-4 gap-2 bg-[var(--bg-primary)] p-3 rounded-2xl border border-[var(--glass-border)]">
                                                <div className="text-center">
                                                    <p className="text-lg font-black text-[var(--text-primary)] tabular-nums">{Math.round(nutritionTotals.cal)}</p>
                                                    <p className="text-[9px] font-bold text-[var(--text-muted)] uppercase tracking-widest">{t('cart.kcal', language)}</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-lg font-black text-[var(--text-primary)] tabular-nums">{Math.round(nutritionTotals.protein)}</p>
                                                    <p className="text-[9px] font-bold text-[var(--text-muted)] uppercase tracking-widest">{t('cart.protein', language)}</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-lg font-black text-[var(--text-primary)] tabular-nums">{Math.round(nutritionTotals.fat)}</p>
                                                    <p className="text-[9px] font-bold text-[var(--text-muted)] uppercase tracking-widest">{t('cart.fat', language)}</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-lg font-black text-[var(--text-primary)] tabular-nums">{Math.round(nutritionTotals.carbs)}</p>
                                                    <p className="text-[9px] font-bold text-[var(--text-muted)] uppercase tracking-widest">{t('cart.carbs', language)}</p>
                                                </div>
                                            </div>
                                        )}
                                        {!hasNutrition && <div className="mb-8" />}
                                        {showConfirm && (
                                          <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowConfirm(false)}>
                                            <div className="bg-[var(--bg-card)] rounded-3xl p-8 max-w-sm mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
                                              <h3 className="text-xl font-bold text-[var(--text-primary)] text-center mb-2">{t('cart.confirmTitle', language)}</h3>
                                              <p className="text-[var(--text-secondary)] text-center text-sm mb-6">
                                                {totalItems} {confirmWord} <span className="font-bold text-[var(--accent-color)]">{discountedTotal} {settings.currency}</span>
                                              </p>
                                              {staffInfo && (
                                                <p className="text-success-fg text-center text-sm mb-4 font-bold">
                                                  {t('cart.staffDiscount', language)} {staffInfo.discount}% ({staffInfo.name})
                                                </p>
                                              )}
                                              <div className="flex gap-3">
                                                <button onClick={() => setShowConfirm(false)} className="flex-1 py-3 rounded-2xl border-2 border-[var(--bg-tertiary)] text-[var(--text-secondary)] font-bold transition-all hover:bg-[var(--bg-tertiary)]">
                                                  {t('cart.confirmBack', language)}
                                                </button>
                                                <button onClick={() => { setShowConfirm(false); doSendOrder(); }} className="flex-1 py-3 rounded-2xl bg-[var(--accent-color)] text-white font-bold shadow-lg transition-all active:scale-95">
                                                  {t('cart.confirmSend', language)}
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                        {settings.minOrderAmount && settings.minOrderAmount > 0 && discountedTotal < settings.minOrderAmount && (
                                          <div className="mb-4 bg-danger-bg-50 border border-danger-bg rounded-2xl px-4 py-3 text-center">
                                            <p className="text-danger-fg text-sm font-bold">
                                              {language === 'ru' ? `Минимальная сумма заказа: ${settings.minOrderAmount} ${settings.currency}` :
                                               language === 'ky' ? `Буйрутманын минималдуу суммасы: ${settings.minOrderAmount} ${settings.currency}` :
                                               language === 'zh' ? `最低订单金额: ${settings.minOrderAmount} ${settings.currency}` :
                                               `Minimum order: ${settings.minOrderAmount} ${settings.currency}`}
                                            </p>
                                          </div>
                                        )}
                                        <button
                                            onClick={handleSendOrder}
                                            disabled={isSending || !!(settings.minOrderAmount && settings.minOrderAmount > 0 && discountedTotal < settings.minOrderAmount)}
                                            className="w-full bg-[var(--accent-color)] text-white py-5 rounded-2xl font-black text-xl shadow-[0_15px_40px_rgba(255,140,0,0.4)] active:scale-[0.98] transition-all disabled:opacity-70 flex justify-center items-center group"
                                        >
                                            {isSending ? (
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    <span>{t('cart.sending', language)}</span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center space-x-3">
                                                    <span>{t('cart.order', language)}</span>
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-6 h-6 group-hover:translate-x-1 transition-transform">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                                                    </svg>
                                                </div>
                                            )}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="flex-1 flex flex-col p-8 overflow-y-auto bg-[var(--bg-primary)] custom-scrollbar relative">
                                    {/* Hide Button */}
                                    <button 
                                        onClick={() => {
                                            setShowReceipt(false);
                                            setIsOpen(false);
                                        }}
                                        className="absolute top-4 right-4 p-2 bg-[var(--bg-tertiary)] rounded-full text-[var(--text-secondary)] hover:text-[var(--accent-color)] transition-colors z-10 flex items-center space-x-1"
                                    >
                                        <span className="text-[10px] font-bold px-2 uppercase tracking-wider">{t('cart.hide', language)}</span>
                                    </button>

                                    {/* Multi-language Instructions */}
                                    <div className="space-y-4 mb-8 text-center">
                                        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border-4 border-white transition-colors ${
                                          currentOrder?.status === 'accepted' ? 'bg-info-bg-50 text-info-fg' : 
                                          currentOrder?.status === 'ready' ? 'bg-success-bg-50 text-success-fg' :
                                          currentOrder?.status === 'completed' ? 'bg-success-bg-50 text-success-fg' :
                                          currentOrder?.status === 'cancelled' ? 'bg-danger-bg-50 text-danger-fg' :
                                          'bg-accent-10 text-accent'
                                        }`}>
                                            {currentOrder?.status === 'new' && (
                                              <div className="w-10 h-10 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
                                            )}
                                            {currentOrder?.status === 'accepted' && (
                                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-10 h-10">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                              </svg>
                                            )}
                                            {currentOrder?.status === 'ready' && (
                                              <Package className="w-10 h-10" />
                                            )}
                                            {currentOrder?.status === 'completed' && (
                                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-10 h-10">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                              </svg>
                                            )}
                                            {currentOrder?.status === 'cancelled' && (
                                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-10 h-10">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                              </svg>
                                            )}
                                        </div>
                                        
                                        <div className="space-y-2">
                                            <p className="text-2xl font-black text-[var(--accent-color)] uppercase tracking-widest">
                                              {currentOrder ? t(`cashier.status.${currentOrder.status}`, language) : t('cart.sending', language)}
                                            </p>
                                            {currentOrder?.orderNumber && (
                                                <p className="text-4xl font-serif font-black text-[var(--text-primary)] mt-2">
                                                    {t('checkout.orderNumber', language)} {currentOrder.orderNumber}
                                                </p>
                                            )}
                                            {/* 4 шага — должны совпадать с CashierDashboard и OrderTracker:
                                                new → accepted → ready → completed. Раньше тут было только 3,
                                                из-за чего при статусе "ready" бар у клиента откатывался назад. */}
                                            <div className="flex justify-center gap-1 mt-4">
                                                {(['new', 'accepted', 'ready', 'completed'] as const).map((s, i) => {
                                                    const order = ['new', 'accepted', 'ready', 'completed'];
                                                    const reached = currentOrder && order.indexOf(currentOrder.status) >= i;
                                                    return (
                                                        <div
                                                            key={s}
                                                            className={`h-2 w-12 rounded-full transition-all duration-500 ${
                                                                reached
                                                                  ? currentOrder.status === 'cancelled' ? 'bg-danger' : 'bg-[var(--accent-color)]'
                                                                  : 'bg-[var(--bg-tertiary)]'
                                                            }`}
                                                        />
                                                    );
                                                })}
                                            </div>
                                            <p className="text-sm text-[var(--text-secondary)] mt-2">
                                              {currentOrder?.status === 'new' ? t('cart.orderSent', language) :
                                               currentOrder?.status === 'accepted' ? t('cart.preparing', language) :
                                               currentOrder?.status === 'ready' ? t('tracker.readyForPickup', language) :
                                               currentOrder?.status === 'completed' ? t('tracker.served', language) :
                                               currentOrder?.status === 'cancelled' ? t('tracker.cancelled', language) : ''}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Client Receipt — тема-aware. В светлой остаётся «бумажный»
                                        белый/чёрный, в тёмной превращается в тёмный «термо-чек»
                                        с приглушённым текстом. Зубчатые края сверху/снизу. */}
                                    <div className="bg-card p-6 shadow-xl relative overflow-hidden receipt-paper receipt-zigzag flex-shrink-0 text-ink font-mono text-sm">
                                        {/* Header */}
                                        <div className="text-center mb-5">
                                            <p className="text-eyebrow text-ink-soft uppercase mb-1">
                                                {t('cart.clientReceipt', language) || 'Кассовый чек'}
                                            </p>
                                            <h3 className="text-xl font-black uppercase tracking-tight font-serif">
                                                {settings.restaurantName[language] || settings.restaurantName.ru}
                                            </h3>
                                            <p className="text-[10px] text-ink-soft mt-1">{t('cart.receiptCompany', language)}</p>
                                            <p className="text-[10px] text-ink-soft">{t('cart.receiptInn', language)}</p>
                                        </div>

                                        <div className="border-t border-dashed border-ink-soft/40 mb-4" />

                                        {/* Order meta */}
                                        <div className="text-center mb-4 space-y-1">
                                            {currentOrder?.orderNumber && (
                                                <p className="text-3xl font-black tracking-tighter">
                                                    №&thinsp;{currentOrder.orderNumber}
                                                </p>
                                            )}
                                            {currentOrder?.tableNumber && (
                                                <p className="text-sm font-bold">
                                                    {tableLabel(currentOrder.tableNumber, language)}
                                                </p>
                                            )}
                                            <div className="flex justify-center gap-3 text-[10px] uppercase tracking-wider text-ink-soft mt-1">
                                                <span>{currentOrder?.orderType === 'dine_in' ? t('checkout.dineIn', language) : t('checkout.takeaway', language)}</span>
                                                <span>•</span>
                                                <span>{currentOrder?.paymentMethod === 'cash' ? t('checkout.cash', language) : t('checkout.card', language)}</span>
                                            </div>
                                            <p className="text-[10px] text-ink-muted mt-1 tabular-nums">
                                                {currentOrder?.createdAt ? new Date(currentOrder.createdAt.seconds * 1000).toLocaleString() : new Date().toLocaleString()}
                                            </p>
                                        </div>

                                        <div className="border-t border-dashed border-ink-soft/40 mb-3" />

                                        {/* Items — цена строки включает допы, иначе
                                            сумма строк не сходится с итогом чека */}
                                        <div className="space-y-2 mb-4">
                                            {displayItems.map((item, idx) => {
                                                const addons = item.selectedAddons || [];
                                                const lineUnit = item.price + addons.reduce((s, a) => s + (a?.price || 0), 0);
                                                return (
                                                    <div key={idx} className="flex justify-between items-start gap-3">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-bold text-xs leading-tight">{item.names?.[language] || item.names?.ru}</p>
                                                            {addons.length > 0 && (
                                                                <p className="text-[10px] text-ink-soft leading-tight">
                                                                    + {addons.map((a) => a?.names?.[language] || a?.names?.ru).filter(Boolean).join(', ')}
                                                                </p>
                                                            )}
                                                            <p className="text-[10px] text-ink-soft tabular-nums">
                                                                {item.quantity} × {lineUnit} {settings.currency}
                                                            </p>
                                                        </div>
                                                        <p className="font-black whitespace-nowrap tabular-nums">
                                                            {lineUnit * item.quantity}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="border-t border-dashed border-ink-soft/40 mb-3" />

                                        {/* Totals */}
                                        <div className="space-y-1 mb-5">
                                            <div className="flex justify-between text-[10px] text-ink-soft tabular-nums">
                                                <span>{t('cart.receiptVat', language)}</span>
                                                <span>{(displayTotal * 0.12).toFixed(2)} {settings.currency}</span>
                                            </div>
                                            <div className="flex justify-between text-[10px] text-ink-soft tabular-nums">
                                                <span>{t('cart.receiptSale', language)}</span>
                                                <span>{displayTotal} {settings.currency}</span>
                                            </div>
                                            <div className="flex justify-between items-baseline pt-2 mt-2 border-t border-ink-soft/30">
                                                <span className="text-eyebrow uppercase text-ink-soft">{t('cart.receiptTotal', language)}</span>
                                                <span className="text-2xl font-black tabular-nums">
                                                    {displayTotal} <span className="text-sm font-bold text-ink-soft">{settings.currency}</span>
                                                </span>
                                            </div>
                                        </div>

                                        {/* Footer: QR со ссылкой на /order/:id —
                                            гость сканирует со своего телефона и видит
                                            статус заказа в реальном времени. */}
                                        <div className="pt-4 border-t border-dashed border-ink-soft/40 flex items-center gap-4">
                                            {currentOrder?.id && (
                                                <div className="bg-white p-1.5 rounded-card-sm flex-shrink-0">
                                                    <img
                                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&margin=0&data=${encodeURIComponent(window.location.origin + '/order/' + currentOrder.id)}`}
                                                        alt="Order status QR"
                                                        className="w-20 h-20 block"
                                                        loading="lazy"
                                                    />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0 text-left">
                                                <p className="text-xs font-bold leading-snug">{t('cart.receiptThanks', language)}</p>
                                                <p className="text-[10px] text-ink-soft mt-0.5">{t('cart.receiptEnjoy', language)}</p>
                                                {currentOrder?.id && (
                                                    <p className="text-[9px] text-ink-muted mt-2 uppercase tracking-wider">
                                                        {t('cart.receiptScan', language)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        onClick={handleFinish}
                                        className="mt-10 w-full bg-accent text-white py-5 rounded-card-md font-black text-xl shadow-accent-glow active:scale-95 transition-all flex-shrink-0"
                                    >
                                        {t('cart.finishOrder', language)}
                                    </button>
                                </div>
                            )}
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};

export default CartWidget;
